import { BadgeColor, BadgeShape, BadgeSize, ExBadge } from '@boomi/exosphere';
import classnames from 'classnames';
import type { DebugTabId } from '../../../types/debugger';
import ScreenRouter from '../../generic/ScreenRouter';
import DebugActionButtons from './DebugActionButtons';
import { useDebugDispatch, useDebugState } from './DebugProvider';
import StateValues from './tabs/StateValues';
import Breakpoints from './tabs/breakpoints/Breakpoints';
import RootFaults from './tabs/rootfaults/RootFaults';

interface DebugTab {
    id: DebugTabId;
    name: string;
    isActive: boolean;
    count: number;
    color: BadgeColor;
}

const DebugTabs = ({ onClose }: { onClose: () => void }) => {
    const { activeTabId, callStack } = useDebugState();
    const dispatch = useDebugDispatch();

    const selectedCallStackItem = callStack.find((callStackItem) => callStackItem.isSelected);

    const tabs: DebugTab[] = [
        {
            id: 'STATE_VALUES',
            name: 'State Values',
            isActive: activeTabId === 'STATE_VALUES',
            count: selectedCallStackItem?.stateValues?.length || 0,
            color: BadgeColor.GRAY,
        },
        {
            id: 'BREAK_POINTS',
            name: 'Assertions',
            isActive: activeTabId === 'BREAK_POINTS',
            count: selectedCallStackItem?.breakpointsHit?.length || 0,
            color: selectedCallStackItem?.breakpointsHit?.length
                ? BadgeColor.GREEN
                : BadgeColor.GRAY,
        },
        {
            id: 'ROOT_FAULTS',
            name: 'Root Faults',
            isActive: activeTabId === 'ROOT_FAULTS',
            count: Object.keys(selectedCallStackItem?.rootFaults || []).length || 0,
            color: Object.keys(selectedCallStackItem?.rootFaults || []).length
                ? BadgeColor.RED
                : BadgeColor.GRAY,
        },
    ];

    const activatedTab = tabs.find((tab) => tab.isActive) as DebugTab;

    return (
        <div className="debug-tabs">
            <div data-testid="tab-header" className="flex gap-small debug-header">
                {tabs.map((tab) => {
                    const classes = classnames({
                        'debug-tab': true,
                        active: tab.isActive,
                    });
                    return (
                        <button
                            key={tab.id}
                            className={classes}
                            onClick={() => dispatch({ type: 'setActiveTab', activeTabId: tab.id })}
                            type="button"
                        >
                            {tab.name}
                            <span className="inline-block margin-left-sml">
                                <ExBadge
                                    size={BadgeSize.SMALL}
                                    color={tab.color}
                                    shape={BadgeShape.ROUND}
                                >{`${
                                    tab.count
                                }${tab.id === 'BREAK_POINTS' ? ' hits' : ''}`}</ExBadge>
                            </span>
                        </button>
                    );
                })}
                <DebugActionButtons onClose={onClose} />
            </div>
            <div className="debug-tab-content">
                <ScreenRouter currentScreen={activatedTab.id}>
                    <StateValues screen="STATE_VALUES" />
                    <Breakpoints screen="BREAK_POINTS" />
                    <RootFaults screen="ROOT_FAULTS" />
                </ScreenRouter>
            </div>
        </div>
    );
};

export default DebugTabs;
